import { useState } from "react";
import axios from "axios";
import { CheckCircle2, ChevronDown, Send } from "lucide-react";
import { Eyebrow, Reveal, goTo } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const FUND_VALUES = ["open_check", "unclaimed_cash", "dormant_account", "not_sure"];
const AMOUNT_VALUES = ["under_1k", "1k_10k", "10k_50k", "over_50k", "not_sure"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-cream/15 bg-ink/60 px-4 py-3 text-sm text-cream placeholder:text-cream/35 outline-none transition-[border-color] duration-300 focus:border-gold/60";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-cream/55";

const Select = ({ value, onChange, options, values, testid, id }) => (
  <div className="relative">
    <select id={id} value={value} onChange={onChange} data-testid={testid} className={`${inputCls} appearance-none pr-10`}>
      {values.map((v, i) => (
        <option key={v} value={v} className="bg-ink text-cream">
          {options[i]}
        </option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" />
  </div>
);

export default function ClaimForm() {
  const { t } = useLang();
  const c = t.claim;
  const [form, setForm] = useState({ name: "", contact: "", fund_type: "not_sure", amount: "not_sure", message: "" });
  const [status, setStatus] = useState("idle");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/claims`, form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="claim" className="relative pb-24 lg:pb-32">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-12">
        <div>
          <Reveal>
            <Eyebrow className="mb-5">{c.eyebrow}</Eyebrow>
            <h2 className="font-display text-4xl font-semibold leading-[1.05] text-cream sm:text-5xl">
              {c.titleA} <span className="italic text-gold-grad">{c.titleB}</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-cream/70">{c.intro}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <ul className="mt-8 space-y-3" data-testid="claim-trust-list">
              {c.trust.map((line) => (
                <li key={line} className="flex items-center gap-3 text-sm text-cream/65">
                  <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                  {line}
                </li>
              ))}
            </ul>
            <p className="mt-8 border-l-2 border-gold/40 pl-4 text-xs leading-relaxed text-cream/45">{c.consent}</p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {status === "success" ? (
            <div
              className="rounded-3xl border border-gold/30 bg-navy2/80 p-10 text-center"
              data-testid="claim-success"
            >
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
              <h3 className="mt-6 font-display text-3xl font-semibold text-cream">{c.successTitle}</h3>
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-cream/65">{c.successBody}</p>
              <button
                onClick={(e) => goTo(e, "#consult")}
                data-testid="claim-success-cta"
                className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em]"
              >
                {c.successCta}
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-3xl border border-gold/20 bg-navy2/70 p-7 sm:p-9"
              data-testid="claim-form"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="claim-name" className={labelCls}>{c.nameLabel}</label>
                  <input id="claim-name" value={form.name} onChange={set("name")} placeholder={c.namePh} className={inputCls} data-testid="claim-name-input" />
                </div>
                <div>
                  <label htmlFor="claim-contact" className={labelCls}>{c.contactLabel}</label>
                  <input id="claim-contact" value={form.contact} onChange={set("contact")} placeholder={c.contactPh} className={inputCls} data-testid="claim-contact-input" />
                </div>
                <div>
                  <label htmlFor="claim-fund" className={labelCls}>{c.fundLabel}</label>
                  <Select id="claim-fund" value={form.fund_type} onChange={set("fund_type")} options={c.fundOptions} values={FUND_VALUES} testid="claim-fund-select" />
                </div>
                <div>
                  <label htmlFor="claim-amount" className={labelCls}>{c.amountLabel}</label>
                  <Select id="claim-amount" value={form.amount} onChange={set("amount")} options={c.amountOptions} values={AMOUNT_VALUES} testid="claim-amount-select" />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="claim-message" className={labelCls}>{c.messageLabel}</label>
                <textarea id="claim-message" value={form.message} onChange={set("message")} placeholder={c.messagePh} rows={3} className={`${inputCls} resize-none`} data-testid="claim-message-input" />
              </div>
              {status === "error" && (
                <p className="mt-4 rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200" data-testid="claim-error">
                  {c.error}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="claim-submit-button"
                className="btn-gold mt-7 flex w-full items-center justify-center gap-2.5 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-[0.14em] disabled:opacity-60"
              >
                {status === "sending" ? c.sending : c.submit}
                <Send className="h-4 w-4" />
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
